import { MAX_AUDIO_BYTES, MAX_AUDIO_MS } from "../app/constants";
import { getAudioFileExtension } from "../media/audioRecorder";
import { apiUrl } from "../platform/runtime";

export interface TranscribeIssueAudioInput {
  audio: Blob;
  issueId: string;
  clientDurationMs: number;
  mimeType: string;
}

export interface TranscribeIssueAudioSuccess {
  ok: true;
  transcript?: string;
  suggestedDescription?: string;
  title: string;
  description: string;
  modelUsed: string;
  needsManualEntry: boolean;
}

export interface TranscribeIssueAudioError {
  ok: false;
  code: string;
  message: string;
}

export type TranscribeIssueAudioResponse =
  | TranscribeIssueAudioSuccess
  | TranscribeIssueAudioError;

export async function transcribeIssueAudio(
  input: TranscribeIssueAudioInput,
  signal?: AbortSignal,
  onProgress?: (event: { phase: "uploading" | "transcribing"; loadedBytes?: number; totalBytes?: number }) => void
): Promise<TranscribeIssueAudioSuccess> {
  if (input.audio.size > MAX_AUDIO_BYTES) {
    throw new Error("This audio note is too large. Please record a shorter note.");
  }
  if (input.clientDurationMs > MAX_AUDIO_MS + 1500) {
    throw new Error(`This audio note is longer than ${MAX_AUDIO_MS / 1000} seconds.`);
  }

  const form = new FormData();
  const extension = getAudioFileExtension(input.mimeType);
  form.append("audio", input.audio, `issue-note.${extension}`);
  form.append("issueId", input.issueId);
  form.append("clientDurationMs", String(Math.round(input.clientDurationMs)));
  form.append("mimeType", input.mimeType);
  form.append("appVersion", import.meta.env.VITE_APP_VERSION || "0.1.0");

  return uploadWithProgress(form, signal, onProgress);
}

function uploadWithProgress(
  form: FormData,
  signal?: AbortSignal,
  onProgress?: (event: { phase: "uploading" | "transcribing"; loadedBytes?: number; totalBytes?: number }) => void
): Promise<TranscribeIssueAudioSuccess> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;

    const fail = (error: Error | DOMException) => {
      if (settled) {
        return;
      }
      settled = true;
      reject(error);
    };

    const abort = () => {
      xhr.abort();
      fail(new DOMException("Transcription request was canceled.", "AbortError"));
    };

    if (signal?.aborted) {
      abort();
      return;
    }

    signal?.addEventListener("abort", abort, { once: true });
    xhr.open("POST", apiUrl("/api/transcribe-issue-audio"));
    xhr.withCredentials = false;
    xhr.timeout = 45_000;

    xhr.upload.onprogress = (event) => {
      onProgress?.({
        phase: "uploading",
        loadedBytes: event.loaded,
        totalBytes: event.lengthComputable ? event.total : undefined
      });
    };
    xhr.upload.onloadend = () => {
      onProgress?.({ phase: "transcribing" });
    };
    xhr.onload = () => {
      signal?.removeEventListener("abort", abort);
      if (settled) {
        return;
      }
      settled = true;
      const payload = parsePayload(xhr.responseText);
      if (xhr.status < 200 || xhr.status >= 300 || !payload.ok) {
        reject(new Error(payload.ok ? "Transcription failed." : payload.message));
        return;
      }
      resolve(payload);
    };
    xhr.onerror = () => {
      signal?.removeEventListener("abort", abort);
      fail(new Error("Network connection was interrupted. This voice note will retry."));
    };
    xhr.ontimeout = () => {
      signal?.removeEventListener("abort", abort);
      fail(new Error("The transcription request timed out. It will retry."));
    };
    xhr.onabort = () => {
      signal?.removeEventListener("abort", abort);
      fail(new DOMException("Transcription request was canceled.", "AbortError"));
    };
    xhr.send(form);
  });
}

function parsePayload(responseText: string): TranscribeIssueAudioResponse {
  try {
    return JSON.parse(responseText) as TranscribeIssueAudioResponse;
  } catch {
    return {
      ok: false,
      code: "BAD_RESPONSE",
      message: "The transcription service returned an unreadable response."
    };
  }
}
